import { useEffect, useState, useRef } from 'react';
import localforage from 'localforage';
import moment from 'moment';
import { message } from 'antd';
import { useOfflineModeContext } from '../context/offlineContext';
import { useAuthContext } from '../context/authContext';
import {
  storeProductImages,
  getLastCacheDate,
  getPendingImageCacheCount,
  CacheMode,
} from '../utils/cacheUtils';

// --- Constants ---
const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000; // Check every 1 hour
const MIN_HOURS_BETWEEN_UPDATES = 24; // Minimum hours before running update again
const LAST_IMAGE_UPDATE_RUN_KEY = 'lastPeriodicImageUpdateRun';

export const usePeriodicImageUpdater = () => {
  const { networkState } = useOfflineModeContext();
  const { authUser } = useAuthContext();

  const [isLoading, setIsLoading] = useState(false);
  const isProcessingRef = useRef(false);
  const intervalIdRef = useRef(null);

  useEffect(() => {
    const runUpdateCheck = async () => {
      // 1. Check conditions before potentially running
      if (isProcessingRef.current) {
        // console.log('[ImageUpdater] Already processing. Skipping check.');
        return;
      }
      if (!networkState?.online) {
        // console.log('[ImageUpdater] Offline. Skipping check.');
        return;
      }
      if (!authUser) {
        console.log('[ImageUpdater] User not authenticated. Skipping check.');
        return;
      }
      isProcessingRef.current = true;
      let hide;
      try {
        // 2. Check timing: Has it been long enough since the last run?
        const lastRunTimestamp = await localforage.getItem(
          LAST_IMAGE_UPDATE_RUN_KEY,
        );
        const hoursSinceLastRun = lastRunTimestamp
          ? moment().diff(moment(lastRunTimestamp), 'hours')
          : MIN_HOURS_BETWEEN_UPDATES + 1; // Ensure it runs the first time

        if (hoursSinceLastRun < MIN_HOURS_BETWEEN_UPDATES) {
          return;
        }

        // 3. Check if we have a base cache date or pending retries (needed for UPDATE mode)
        const lastCacheDate = await getLastCacheDate();
        if (!lastCacheDate && !(await getPendingImageCacheCount())) {
          console.info(
            '[ImageUpdater] Cannot run UPDATE mode without a last cache date. Run initial sync/refresh first.',
          );
          await localforage.setItem(
            LAST_IMAGE_UPDATE_RUN_KEY,
            new Date().toISOString(),
          );
          return;
        }

        // 4. Conditions met - Start the update process
        setIsLoading(true);
        hide = message.loading('Automatsko ažuriranje slika...', 0);

        const results = await storeProductImages(
          null, // products - not needed for UPDATE mode
          null, // onProgress - not needed for background task
          CacheMode.UPDATE, // mode
          lastCacheDate, // lastUpdateCheckDate
        );

        console.log('[ImageUpdater] Periodic image update finished.', results);

        // 5. Record the successful run time
        await localforage.setItem(
          LAST_IMAGE_UPDATE_RUN_KEY,
          new Date().toISOString(),
        );
      } catch (error) {
        console.error(
          '[ImageUpdater] Error during periodic update check/run:',
          error,
        );

        // Only show error message if it's not a network error
        const isNetworkError = error.code === 'ECONNABORTED' ||
                               /timeout of \d+ms exceeded/.test(error.message || '') ||
                               error.message?.includes('Network Error') ||
                               error.message?.includes('network error') ||
                               error.message?.includes('nedostupan');

        if (!isNetworkError) {
          message.error('Greška tokom automatskog ažuriranja slika.', 3);
        }
      } finally {
        isProcessingRef.current = false;
        setIsLoading(false);

        if (hide) {
          hide();
        }
      }
    }; // --- End of runUpdateCheck ---

    // --- Set up the interval ---
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
    }

    runUpdateCheck();

    intervalIdRef.current = setInterval(
      runUpdateCheck,
      UPDATE_CHECK_INTERVAL_MS,
    );

    // --- Cleanup function ---
    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser, networkState?.online]);

  return { isImageUpdaterLoading: isLoading };
};
